import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Search,
  ChevronDown,
  ArrowDown,
  ArrowUp,
  Clock,
  Calendar,
  CheckCircle2,
  User,
  Check,
  type LucideIcon,
} from 'lucide-react';
import { DriverCall } from '@/services/driverApi';
import { useDriverStateV2 } from '@/hooks/useDriverStateV2';
import { removeCountryFromAddress } from '@/utils/addressFormatter';
import {
  filterVisibleDispatches,
  filterCanceledDispatchesForCallsViews,
} from '@/utils/dispatchFilter';
import { SearchInputWithCustomKeyboard } from './common/SearchInputWithCustomKeyboard';

interface Customer {
  id: string;
  accountName: string;
  [key: string]: any;
}

// Get the approval status text for URGENTLY calls
const getUrgentlyApprovalStatus = (status: string) => {
  switch (status) {
    case 'COMPLETED_AWAITING':
      return 'Awaiting Approval';
    case 'GOA_AWAITING':
      return 'GOA Awaiting Approval';
    case 'COMPLETED':
      return 'Completion Approved';
    case 'GOA':
      return 'GOA Approved';
    default:
      return null;
  }
};

// Function to get status color based on dispatch status - matches CallScreen.tsx
const getStatusColor = (status: string) => {
  // Convert backend status to frontend status (matching CallScreen.tsx)
  const statusMap: { [key: string]: string } = {
    'NEW': 'ready',
    'PENDING': 'waiting',
    'DISPATCHED': 'en_route',
    'ON_SCENE': 'on_scene',
    'TOWING': 'towing',
    'COMPLETED': 'complete',
    'COMPLETED_AWAITING': 'complete',
    'GOA': 'complete',
    'GOA_AWAITING': 'complete',
    'FAILED': 'canceled',
    'DECLINED': 'canceled',
    'CANCELED': 'canceled',
    'CANCELLED': 'canceled',
  };
  
  const mappedStatus = statusMap[status] || status;
  
  // Return hex color pairs that match Tailwind classes used in CallScreen
  switch (mappedStatus) {
    case 'ready': // NEW - green-600
      return { bg: '#dcfce7', text: '#166534' }; // light green background
    case 'waiting': // PENDING - yellow-600
      return { bg: '#fef3c7', text: '#92400e' }; // light yellow background
    case 'en_route': // DISPATCHED - blue-600
      return { bg: '#dbeafe', text: '#0369a1' }; // light blue background
    case 'on_scene': // ON_SCENE - orange-600
      return { bg: '#fed7aa', text: '#92400b' }; // light orange background
    case 'towing': // TOWING - teal-600
      return { bg: '#ccfbf1', text: '#0d9488' }; // light teal background
    case 'complete': // COMPLETED, COMPLETED_AWAITING, GOA, GOA_AWAITING - purple-600
      return { bg: '#e9d5ff', text: '#6b21a8' }; // light purple background
    case 'canceled': // FAILED - grey
      return { bg: '#f3f4f6', text: '#4b5563' }; // light gray background
    default:
      return { bg: '#f3f4f6', text: '#4b5563' }; // light gray background
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'COMPLETED':
    case 'COMPLETED_AWAITING':
      return 'Completed';
    case 'DECLINED':
      return 'Declined';
    case 'CANCELED':
    case 'CANCELLED':
      return 'Canceled';
    case 'ON_SCENE':
    case 'TOWING':
      return 'In Progress';
    case 'GOA_AWAITING':
    case 'GOA':
      return 'GOA';
    default:
      return status.charAt(0) + status.slice(1).toLowerCase();
  }
};

export type CallsSortFilterOption =
  | 'Newest first'
  | '24 hours'
  | '7 days'
  | 'Calls completed'
  | 'Completed by me'
  | 'Oldest first';

const SORT_FILTER_OPTIONS: {
  value: CallsSortFilterOption;
  icon: LucideIcon;
}[] = [
  { value: 'Newest first', icon: ArrowDown },
  { value: '24 hours', icon: Clock },
  { value: '7 days', icon: Calendar },
  { value: 'Calls completed', icon: CheckCircle2 },
  { value: 'Completed by me', icon: User },
  { value: 'Oldest first', icon: ArrowUp },
];

interface CallsSortFilterDropdownProps {
  filterOption: CallsSortFilterOption;
  onFilterOptionChange: (option: CallsSortFilterOption) => void;
}

const CallsSortFilterDropdown: React.FC<CallsSortFilterDropdownProps> = ({
  filterOption,
  onFilterOptionChange,
}) => {
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    right: number;
    minWidth: number;
  } | null>(null);

  const updateMenuPosition = useCallback(() => {
    if (!filterButtonRef.current) return;
    const rect = filterButtonRef.current.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
      minWidth: Math.max(rect.width, 220),
    });
  }, []);

  useEffect(() => {
    if (!showFilterPopup) return;
    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [showFilterPopup, updateMenuPosition]);

  useEffect(() => {
    if (!showFilterPopup) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        filterButtonRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setShowFilterPopup(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilterPopup]);

  const handleSelect = (value: CallsSortFilterOption) => {
    onFilterOptionChange(value);
    setShowFilterPopup(false);
  };

  const handleToggle = () => {
    if (!showFilterPopup) {
      updateMenuPosition();
    }
    setShowFilterPopup((open) => !open);
  };

  return (
    <div className="relative shrink-0 w-36">
      <button
        ref={filterButtonRef}
        type="button"
        onClick={handleToggle}
        className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-background text-sm font-medium text-gray-800 flex items-center justify-between gap-2 min-w-0"
        aria-expanded={showFilterPopup}
        aria-haspopup="listbox"
      >
        <span className="truncate min-w-0">{filterOption}</span>
        <ChevronDown size={16} className="text-gray-500 flex-shrink-0" />
      </button>
      {showFilterPopup &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            data-dispatch-filter-popup
            role="listbox"
            className="fixed bg-background border border-gray-200 rounded-md shadow-lg z-[9999] py-1"
            style={{
              top: menuPosition.top,
              right: menuPosition.right,
              minWidth: menuPosition.minWidth,
            }}
          >
            {SORT_FILTER_OPTIONS.map(({ value, icon: Icon }) => (
              <button
                key={value}
                type="button"
                role="option"
                aria-selected={filterOption === value}
                onClick={() => handleSelect(value)}
                className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-gray-50 transition-colors text-gray-700"
              >
                <Icon size={16} className="text-gray-400 flex-shrink-0" />
                <span className="flex-1 text-left text-sm">{value}</span>
                {filterOption === value && (
                  <Check size={16} className="text-primary flex-shrink-0" />
                )}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

// Statuses that indicate a call is done (excluded from "Active Only")
const DONE_STATUSES = [
  'COMPLETED',
  'COMPLETED_AWAITING',
  'DECLINED',
  'GOA',
  'GOA_AWAITING',
  'FAILED',
  'CANCELED',
  'CANCELLED',
];

interface ActiveServiceCallsTableProps {
  dispatches: DriverCall[];
  customers?: Customer[];
  showHeader?: boolean;
  onCallClick?: (callId: string) => void;
  onAddCall?: () => void;
  showViewAllButton?: boolean;
  onViewAllClick?: () => void;
  showFilterButton?: boolean;
  /** When false (default), hide canceled jobs — home "Calls" card. Dispatch tab passes true. */
  showCanceledDispatches?: boolean;
  /** When set, limits rendered rows to the first N items after filtering/sorting. */
  maxRows?: number;
}

export const ActiveServiceCallsTable: React.FC<ActiveServiceCallsTableProps> = ({
  dispatches,
  customers = [],
  showHeader = true,
  onCallClick,
  onAddCall,
  showViewAllButton = false,
  onViewAllClick,
  showFilterButton = true,
  showCanceledDispatches = false,
  maxRows,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOption, setFilterOption] = useState<CallsSortFilterOption>('Newest first');
  const { driver } = useDriverStateV2();

  const getAccountName = (accountId: string): string => {
    const customer = customers.find(c => c.id === accountId);
    return customer?.accountName || accountId;
  };

  // Check if current driver is assigned to a call
  const isCurrentDriverAssigned = (dispatch: DriverCall): boolean => {
    if (!driver?.id) return false;
    return dispatch.drivers?.some(d => d.id === driver.id) || false;
  };

  // Filter out ACCEPTED_AWAITING, apply sort/filter option, then search
  const filteredDispatches = useMemo(() => {
    let visible = filterVisibleDispatches(dispatches);
    visible = filterCanceledDispatchesForCallsViews(visible, !showCanceledDispatches);

    const now = new Date();

    if (filterOption === 'Newest first') {
      // Preserve parent's incoming order so "Me on top" pinning is kept
    } else if (filterOption === 'Oldest first') {
      visible = [...visible].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
    } else if (filterOption === '24 hours') {
      const cutoff = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      visible = visible
        .filter(d => {
          const date = new Date(d.completionTime || d.createdAt);
          return date >= cutoff && date <= now;
        })
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    } else if (filterOption === '7 days') {
      const cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      visible = visible
        .filter(d => {
          const date = new Date(d.completionTime || d.createdAt);
          return date >= cutoff && date <= now;
        })
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    } else if (filterOption === 'Calls completed') {
      visible = visible
        .filter(d => DONE_STATUSES.includes(d.status))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    } else if (filterOption === 'Completed by me') {
      visible = visible
        .filter(
          d =>
            DONE_STATUSES.includes(d.status) &&
            d.drivers?.some(dr => dr.id === driver?.id),
        )
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }

    if (!searchQuery.trim()) {
      return visible;
    }

    const query = searchQuery.toLowerCase().trim();
    return visible.filter(
      dispatch =>
        dispatch.dispatchNumber?.toLowerCase().includes(query) ||
        dispatch.vehicle?.licensePlate?.toLowerCase().includes(query) ||
        dispatch.vehicle?.vin?.toLowerCase().includes(query),
    );
  }, [dispatches, searchQuery, filterOption, showCanceledDispatches, driver?.id]);

  const displayedDispatches = maxRows ? filteredDispatches.slice(0, maxRows) : filteredDispatches;

  return (
    <div className="border border-gray-200 rounded-lg">
      {/* Header Row */}
      {showHeader && (
        <div className="pl-4 pr-5 py-3 flex items-center justify-between gap-3 bg-gray-100 border-b border-gray-200 min-w-0">
          <h3 className="text-lg font-semibold text-black shrink-0">Calls</h3>
          {showViewAllButton ? (
            <div className="flex items-center gap-2 flex-shrink-0">
              {showFilterButton && (
                <CallsSortFilterDropdown
                  filterOption={filterOption}
                  onFilterOptionChange={setFilterOption}
                />
              )}
              <button
                onClick={onViewAllClick}
                className="px-4 py-2 bg-transparent text-primary rounded-lg font-medium hover:bg-gray-100 transition-colors text-sm flex items-center gap-2"
              >
                View All
                <span className="text-primary">→</span>
              </button>
            </div>
          ) : (
            <div className="flex flex-1 items-center gap-2 min-w-0 justify-end">
              {dispatches.length > 0 && (
                <div className="flex flex-1 items-center gap-2 min-w-0 basis-0">
                  <Search size={18} className="text-gray-500 shrink-0" />
                  <SearchInputWithCustomKeyboard
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Call #, Plate, VIN"
                    onClear={() => setSearchQuery('')}
                    className="flex-1 min-w-0"
                  />
                </div>
              )}
              {showFilterButton && (
                <CallsSortFilterDropdown
                  filterOption={filterOption}
                  onFilterOptionChange={setFilterOption}
                />
              )}
            </div>
          )}
        </div>
      )}
      {/* Call Rows or Empty State */}
      <div className="space-y-0 overflow-hidden rounded-b-lg">
        {filteredDispatches.length > 0 ? (
          <>
            {displayedDispatches.map((dispatch, index) => {
              const statusColor = getStatusColor(dispatch.status);
              // Determine which icon to use based on source
              const iconSrc = dispatch.source === 'URGENTLY' 
                ? '/urgently.jpg' 
                : '/relay-icon.png';

              return (
                <div 
                  key={dispatch.id}
                  onClick={() => onCallClick?.(dispatch.id)}
                  className={`cursor-pointer hover:bg-gray-50 transition-colors ${index !== displayedDispatches.length - 1 ? 'border-b border-gray-200' : 'border-b border-gray-200'}`}
                >
                  {/* Top row: Icon, Call Number/Address, Status */}
                  <div className="px-4 py-3 flex items-start gap-4">
                    {/* Column 1: Circular Icon */}
                    <div className="flex items-center flex-shrink-0">
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border border-gray-200 bg-gray-100">
                        <img 
                          src={iconSrc}
                          alt={dispatch.source === 'URGENTLY' ? 'Urgently' : 'Relay'}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Fallback to logo if image fails to load
                            (e.target as HTMLImageElement).src = '/logo.png';
                          }}
                        />
                      </div>
                    </div>

                    {/* Column 2: Call Number and Address (2 rows) */}
                    <div className="flex-1 flex flex-col justify-start min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-black">#{dispatch.dispatchNumber}</p>
                        {dispatch.source === 'URGENTLY' && getUrgentlyApprovalStatus(dispatch.status) && (
                          <span className="text-xs font-medium text-gray-600">
                            {getUrgentlyApprovalStatus(dispatch.status)}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 truncate">{removeCountryFromAddress(dispatch.pickupLocation)}</p>
                      <p className="text-xs text-gray-500 mt-1">{getAccountName(dispatch.account) || dispatch.source || 'No Account'}</p>
                    </div>

                    {/* Column 3: Status Badge (2 rows, top badge, bottom empty) */}
                    <div className="flex flex-col justify-start flex-shrink-0">
                      <div className="flex items-center gap-2">
                        {isCurrentDriverAssigned(dispatch) && (
                          <span 
                            className="px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap"
                            style={{
                              backgroundColor: '#E0E7FF',
                              color: '#3730A3',
                            }}
                          >
                            Me
                          </span>
                        )}
                        <span 
                          className="px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap"
                          style={{
                            backgroundColor: statusColor.bg,
                            color: statusColor.text,
                          }}
                        >
                          {getStatusLabel(dispatch.status)}
                        </span>
                      </div>
                      {/* Bottom row is empty as requested */}
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Add Call Button */}
            {onAddCall && (
              <button
                onClick={onAddCall}
                className="w-full px-4 py-3 flex items-center justify-center gap-2 text-black hover:bg-gray-50 transition-colors border-t border-gray-200"
              >
                <span className="text-lg">+</span>
                <span className="text-sm font-medium">Add Call</span>
              </button>
            )}
          </>
        ) : (
          <div className="px-4 py-3">
            <p className="text-sm text-gray-400">No calls found.<br />To view older calls, please log in to our Desktop Application</p>
          </div>
        )}
      </div>
    </div>
  );
};

